type FocusRestoreElement = {
  focus?: unknown;
  tagName?: string;
};

type FocusRestoreDocument = {
  activeElement: FocusRestoreElement | null;
  body: FocusRestoreElement;
  getElementById: (id: string) => FocusRestoreElement | null;
};

type RestoreTextareaFocusAfterChangeParams = {
  documentRef?: FocusRestoreDocument;
  fieldName: string;
  schedule?: (callback: () => void) => void;
};

const isFocusableTextarea = (
  element: FocusRestoreElement | null,
): element is FocusRestoreElement & { focus: () => void } =>
  element?.tagName === 'TEXTAREA' && typeof element.focus === 'function';

export const restoreTextareaFocusAfterChange = (params: RestoreTextareaFocusAfterChangeParams): void => {
  const {
    documentRef = document,
    fieldName,
    schedule = (callback) => {
      requestAnimationFrame(callback);
    },
  } = params;

  const restoreFocus = (): void => {
    // Only act when the change blurred the field to the body (the focus-loss symptom);
    // a textarea that kept focus is already the activeElement, so this is a no-op.
    if (documentRef.activeElement !== documentRef.body) return;

    // The field's id is its TanStack name, unique within the form, so getElementById
    // targets this exact textarea rather than scanning a document-wide name list.
    const textarea = documentRef.getElementById(fieldName);
    if (isFocusableTextarea(textarea)) textarea.focus();
  };

  restoreFocus();
  schedule(restoreFocus);
};
